import { ChangeEventHandler, SetStateAction, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Upload, UploadProps, Input, Button } from '@ets/design-system';
import { useDebounce, useDispatch, useSelector } from '_common/hooks';

import { updateModal } from '_common/modals/ModalsSlice';
import { selectReadOnlyMode } from 'Editor/redux/EditorStatusSlice';
import { importCitations } from 'Editor/redux/CitationsSlice';

import CitationsList from '../CitationsList/CitationsList';
import CitationInformation from '../CitationInformation/CitationInformation';
import NavigateToDoDOC from '../NavigateToDoDOC/NavigateToDoDOC';

import styles from './ImportCitations.module.scss';
import { getSanitizedEntityName } from '../utils';

type ImportCitationsProps = {
  navigationDoDOC?: boolean;
  setNavigationDoDOC: (value: SetStateAction<boolean>) => void;
};

const ImportCitations = ({ navigationDoDOC, setNavigationDoDOC }: ImportCitationsProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const isReadOnlyMode = useSelector(selectReadOnlyMode);
  const citations = useSelector((state) => state.modals.CitationsModal.import) as {
    dropzone: boolean;
    loading: boolean;
    list: ObjectId[];
    dict: Record<ObjectId, Citation>;
  };
  const selected = useSelector((state) => state.table.selected);

  const [filter, setFilter] = useState('');
  const debouncedFilter = useDebounce(filter, 300);
  const filteredCitations = useMemo(
    () => ({
      ...citations,
      list: citations?.list?.filter((id) => {
        // @ts-expect-error CitationSchema issue: missing 'journal'
        const { title, author: authors, journal, year } = citations.dict[id];
        return (
          filter === '' ||
          (title && title.toLowerCase().includes(filter.toLowerCase())) ||
          (authors &&
            !!authors.find((author) =>
              getSanitizedEntityName(author)?.toLowerCase().includes(filter.toLowerCase()),
            )) ||
          (journal && journal.toLowerCase().includes(filter.toLowerCase())) ||
          (year && year.toString().includes(filter.toString()))
        );
      }),
    }),
    [citations, debouncedFilter],
  );

  const setDropzoneVisible = (visible: boolean) => {
    dispatch(
      updateModal({
        modal: 'CitationsModal',
        data: {
          import: {
            ...citations,
            dropzone: visible,
          },
        },
      }),
    );
  };

  const handleFilterChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setFilter(e.target.value);
  };

  const handleCitationsImport: UploadProps['onDrop'] = async (acceptedFiles) => {
    if (acceptedFiles[0]) {
      dispatch(importCitations({ file: acceptedFiles[0] }));
    } else {
      setDropzoneVisible(false);
    }
  };

  const renderImportedContent = () => {
    if (navigationDoDOC) {
      return (
        <NavigateToDoDOC
          menuOptions={[
            { label: 'myFiles', icon: 'folders', identity: 'storage' },
            { label: 'shared', icon: 'folder-user', identity: 'shared' },
            { label: 'spaces', icon: 'box-archive', identity: 'spaces' },
          ]}
          menuSelected={{ label: 'myFiles', icon: 'folders', identity: 'storage' }}
          title={intl.formatMessage({ id: 'IMPORT_FILES_FROM_DODOC' })}
        />
      );
    }
    return (
      <>
        <div className={styles.list}>
          <div className={styles.filter}>
            <Input
              prefix="magnifying-glass"
              value={filter}
              onChange={handleFilterChange}
              placeholder={intl.formatMessage({ id: 'global.filter' })}
              disabled={isReadOnlyMode}
              width="100%"
              size="m"
              testId="import-citations-search"
            />
          </div>
          <CitationsList
            loading={citations.loading}
            citations={filteredCitations}
            handleOnImportClick={() => {
              dispatch(
                updateModal({
                  modal: 'CitationsModal',
                  data: {
                    import: {
                      dropzone: true,
                    },
                  },
                }),
              );
            }}
          />
        </div>
        <div className={styles.information}>
          <CitationInformation
            citationId={Object.keys(selected)[0]}
            disabled
            multiple={Object.keys(selected).length > 1}
          />
        </div>
      </>
    );
  };

  const renderDropzone = () => {
    return (
      <div className={styles.dropzone}>
        <Upload
          size="m"
          name="upload"
          icon="cloud-arrow-up"
          onDrop={handleCitationsImport}
          accept={{
            'text/plain': ['.bib'],
            'application/xml': ['.xml'],
          }}
          testId="upload-citations-input"
          className="w-full"
        >
          <FormattedMessage id="editor.modals.citations.dropFile" />
          <div className={styles.content}>
            <Button
              variant="standard"
              size="m"
              style={{ margin: '2rem 2rem 0 0' }}
              testId={`upload-input-label`}
            >
              <FormattedMessage id="BROWSE_YOUR_COMPUTER" />
            </Button>
            <Button
              variant="standard"
              size="m"
              onClick={(e) => {
                e.stopPropagation();
                setNavigationDoDOC(true);
                setDropzoneVisible(false);
              }}
              style={{ margin: '2rem 2rem 0 0' }}
              testId={`upload-input-label`}
            >
              <FormattedMessage id="BROWSE_DODOC" />
            </Button>
          </div>
        </Upload>
      </div>
    );
  };

  return (
    <div className={`${styles.root} ${citations.dropzone && styles.drop}`}>
      {citations.dropzone ? renderDropzone() : renderImportedContent()}
    </div>
  );
};

export default ImportCitations;
